// ---------------------------------------------------------------------------
// Text colors for the RichEditor — Allyson, 2026-09-09: "Color so I can make
// texts red or blue or another color."
//
// A color is a CLASS (`cg-ink-red`), never a style="" value. Two reasons:
//   1. The app deliberately treats every inline color as foreign: RichEditor
//      scrubs style colors at save and index.css forces `.cg-prose [style]`
//      back to the theme (pasted Google-Docs black on the dark canvas, 7/27).
//      A class rides past both untouched — the CSS comment even says so.
//   2. One class renders as a different value per theme (index.css tokens), so
//      "red" reads on the dark canvas AND on the light cream page. A baked hex
//      can only be right for one of them.
//
// How it gets applied: contentEditable has no "wrap the selection in a class"
// command, but `foreColor` already knows how to split and merge inline runs
// correctly across partial selections. So we run foreColor with a SENTINEL
// color nobody would ever pick (#0100NN), then walk the editor and turn every
// sentinel-colored element into the class. `adoptInkColor` is that walk.
// ---------------------------------------------------------------------------

export interface InkColor {
  key: string;
  label: string;
  cls: string;
  /** Hex handed to execCommand('foreColor'); converted to the class right after. */
  sentinel: string;
}

export const INK_COLORS: InkColor[] = [
  { key: 'red', label: 'Red', cls: 'cg-ink-red', sentinel: '#010001' },
  { key: 'orange', label: 'Orange', cls: 'cg-ink-orange', sentinel: '#010002' },
  { key: 'yellow', label: 'Yellow', cls: 'cg-ink-yellow', sentinel: '#010003' },
  { key: 'green', label: 'Green', cls: 'cg-ink-green', sentinel: '#010004' },
  { key: 'blue', label: 'Blue', cls: 'cg-ink-blue', sentinel: '#010005' },
  { key: 'purple', label: 'Purple', cls: 'cg-ink-purple', sentinel: '#010006' },
  { key: 'pink', label: 'Pink', cls: 'cg-ink-pink', sentinel: '#010007' },
];

/** The sentinel that means "back to the default ink" (key null). */
export const INK_CLEAR_SENTINEL = '#010000';

const INK_CLASSES = INK_COLORS.map((c) => c.cls);

export function inkSentinel(key: string | null): string {
  if (key === null) return INK_CLEAR_SENTINEL;
  const c = INK_COLORS.find((x) => x.key === key);
  if (!c) throw new Error(`unknown ink color: ${key}`);
  return c.sentinel;
}

/** Parse "#rrggbb" or "rgb(r, g, b)" / "rgba(r, g, b, a)" to a triple; null otherwise. */
export function parseColor(s: string | null | undefined): [number, number, number] | null {
  if (!s) return null;
  const v = s.trim().toLowerCase();
  const hex = /^#([0-9a-f]{6})$/.exec(v);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(v);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

function sameColor(a: [number, number, number] | null, b: [number, number, number] | null): boolean {
  return !!a && !!b && a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

/** Which sentinel (if any) an element is carrying — via style="color" (what
 *  styleWithCSS=true produces) or a legacy <font color> (what some engines
 *  produce regardless). */
function sentinelOn(el: HTMLElement): string | null {
  const inline = parseColor(el.style?.color) ?? (el.tagName === 'FONT' ? parseColor(el.getAttribute('color')) : null);
  if (!inline) return null;
  if (sameColor(inline, parseColor(INK_CLEAR_SENTINEL))) return INK_CLEAR_SENTINEL;
  for (const c of INK_COLORS) if (sameColor(inline, parseColor(c.sentinel))) return c.sentinel;
  return null;
}

function stripInlineColor(el: HTMLElement) {
  el.style.removeProperty('color');
  if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
  if (el.tagName === 'FONT') el.removeAttribute('color');
}

/** A wrapper that carries nothing anymore (no attributes, no classes) is noise
 *  — replace it with its children so "back to default" leaves clean HTML. */
function unwrapIfBare(el: HTMLElement) {
  if (el.attributes.length > 0) return;
  if (el.tagName !== 'SPAN' && el.tagName !== 'FONT') return;
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

/**
 * Convert every sentinel-colored element under `root` into its class form.
 * Called right after execCommand('foreColor', sentinel), and again at save so a
 * sentinel that slipped through (typing after a collapsed-caret pick) never
 * reaches the record as a near-black inline color.
 * Returns true when anything changed.
 */
export function adoptInkColor(root: HTMLElement): boolean {
  let changed = false;
  const hits: Array<[HTMLElement, string]> = [];
  root.querySelectorAll<HTMLElement>('[style], font[color]').forEach((el) => {
    const s = sentinelOn(el);
    if (s) hits.push([el, s]);
  });
  for (const [el, sentinel] of hits) {
    stripInlineColor(el);
    el.classList.remove(...INK_CLASSES);
    if (sentinel !== INK_CLEAR_SENTINEL) {
      const c = INK_COLORS.find((x) => x.sentinel === sentinel)!;
      el.classList.add(c.cls);
    }
    if (!el.classList.length) el.removeAttribute('class');
    unwrapIfBare(el);
    changed = true;
  }
  return changed;
}
